import * as React from 'react'
import { ReactElement } from 'react'
import InvoiceForm from '../../../components/Form/Finance/Invoice/InvoiceForm'
import {
    InvoiceDto,
    InvoiceMutationDto,
    LineItemDto,
    LineItemGroupDto,
    LineItemGroupMutationDto,
    LineItemMutationDto,
} from '../../../config/finance/invoiceConfig'
import Loading from '../../../components/Loading'
import { getDataEmptyState } from '../../../context/AxiosContext'
import { useParams } from 'react-router-dom'
import useEntity from '../../../hooks/useEntity'
import { EntityParams } from '../../../components/Form/Form'
import { MessageBar, MessageBarType, Stack, Text } from '@fluentui/react'
import { PricePrecedence, PriceSummaryDto } from '../../../config/finance/financeConfig'
import useAttributes, { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'

const mapPriceSummary = (price?: PriceSummaryDto): string => {
    switch (price?.precedence) {
        case PricePrecedence.Total:
            return price.total
        case PricePrecedence.Subtotal:
            return price.subtotal
        default:
            return undefined
    }
}

export const sortLineItemGroups = (a: LineItemGroupMutationDto, b: LineItemGroupMutationDto): number => {
    // Reverse order on File reference
    const fileCompare: number = (b.file?.reference || 'zzz').localeCompare(a.file?.reference || 'zzz')

    if (fileCompare === 0) {
        if (a.order === b.order) {
            return a.description.localeCompare(b.description)
        }

        return a.order < b.order ? -1 : 1
    }

    return fileCompare
}

const mapInvoice = (invoice: InvoiceDto): InvoiceMutationDto => ({
    id: invoice.id,
    organisationProfileKey: invoice.organisationProfileKey,
    type: invoice.type,
    templateId: invoice.template?.id,
    invoiceApprovalId: invoice.invoiceApprovalId,
    parentId: invoice.parent?.id,
    transaction: invoice.transaction,
    status: invoice.status,
    fileIds: invoice.files?.map(file => file.id),
    clientPivotId: invoice.recipient?.clientId,
    debtorPivotId: invoice.recipient?.debtorId,
    number: invoice.number,
    name: invoice.name,
    description: invoice.description,
    note: invoice.note,
    date: invoice.date,
    term: invoice.term,
    factor: invoice.factor,
    lineItemGroups: invoice.lineItemGroups
        ?.map(
            (lineItemGroup: LineItemGroupDto): LineItemGroupMutationDto => ({
                id: lineItemGroup.id,
                fileId: lineItemGroup.file.id,
                fixedPriceId: lineItemGroup.fixedPrice?.id,
                file: lineItemGroup.file,
                description: lineItemGroup.description,
                order: lineItemGroup.order,
                price: mapPriceSummary(lineItemGroup.price),
                precedence: lineItemGroup.price?.precedence,
                showStatement: lineItemGroup.showStatement,
                consolidation: lineItemGroup.consolidation,
                vatType: lineItemGroup.vatType || undefined,
                lineItems: lineItemGroup.lineItems.map(
                    (lineItem: LineItemDto): LineItemMutationDto => ({
                        id: lineItem.id,
                        type: lineItem.type,
                        entityId: lineItem.entityId,
                    }),
                ),
                accounting: lineItemGroup.accounting,
            }),
        )
        .sort(sortLineItemGroups),
    children: invoice.children?.map((child: InvoiceDto): InvoiceMutationDto => mapInvoice(child)),
    language: invoice.language,
    currency: invoice.price?.currency,
    paymentPrice: invoice.paymentPrice?.amount,
    recipientEmail: invoice.recipient?.email,
    recipientReference: invoice.recipient?.reference,
    messageSubject: invoice.message?.subject,
    messageBody: invoice.message?.body,
    paymentMessageBody: invoice.paymentMessage?.body,
    period: invoice.period,
    unit: invoice.unit,
    accounting: { ...invoice.accounting, transaction: invoice.transaction },
})

export default function EditInvoice(): ReactElement {
    const { id } = useParams<EntityParams>()
    const [entity, setEntity] = useEntity<InvoiceDto>((id: string) => ({ url: `/finance/invoice/${id}` }), id)

    const attributes = useAttributes(AttributeViewType.Input, [AttributeContextType.Invoice], undefined, EntityAttributeType.Invoice)

    if (entity.rejected) {
        return (
            <MessageBar messageBarType={MessageBarType.error} isMultiline>
                <Stack>
                    <Text variant="smallPlus">De declaratie kon niet geladen worden.</Text>
                    <Text variant="smallPlus">
                        Melding: <em>{entity.error.message}</em>
                    </Text>
                </Stack>
            </MessageBar>
        )
    }

    if (!entity.loaded) {
        return <Loading />
    }

    return (
        <InvoiceForm
            invoice={entity.data}
            formikProps={{
                initialValues: mapInvoice(entity.data),
                onSubmit: () => {
                    return Promise.resolve({
                        url: `/finance/invoice/${id}`,
                        method: 'PUT',
                    })
                },
                onReset: () => setEntity(getDataEmptyState<InvoiceDto>()),
            }}
            attributes={attributes}
        />
    )
}
